import { createColumnHelper } from "@tanstack/react-table";
import { MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useNavigate } from "react-router-dom";
import {
  formatProductTagValue,
  getProductTagStyle,
  getRoutes,
} from "@/utils/helpers";
import { Product } from "@/types/product";
import { Image, HelpCircle } from "lucide-react";

const columnHelper = createColumnHelper<Product>();

const useProducts = () => {
  const navigate = useNavigate();
  const routes = getRoutes();

  const columns = [
    columnHelper.accessor("title", {
      header: "Product",
      size: 300,
      cell: (info) => {
        const product = info.row.original;
        const image = product.thumbnail;
        return (
          <div className="flex items-center space-x-2">
            {image ? (
              <div className="h-14 w-14">
                <img
                  src={image}
                  alt={product.title}
                  className="h-full w-full object-contain"
                />
              </div>
            ) : (
              <div className="h-14 w-14 flex items-center justify-center bg-gray-100 rounded-sm text-gray-500 text-xs">
                <Image />
              </div>
            )}
            <div className="font-medium">{product.title}</div>
          </div>
        );
      },
    }),
    columnHelper.accessor("tags", {
      id: "Tags",
      header: "Tags",
      size: 300,
      cell: (info) => {
        const tags = info.getValue();

        return (
          <div>
            <div className="flex flex-wrap gap-1">
              {tags.map((tag) => (
                <span
                  key={tag.id}
                  className={`text-xs px-2 py-1 rounded border font-medium ${getProductTagStyle(tag)}`}
                  title={tag.value}
                >
                  {formatProductTagValue(tag.value)}
                </span>
              ))}
            </div>
          </div>
        );
      },
    }),
    columnHelper.accessor("status", {
      header: "Status",
      cell: (info) => {
        const status = info.row.original.status;
        const statusClass =
          status === "published"
            ? "text-green-500 border border-green-300 bg-green-100 rounded-md"
            : status === "draft"
              ? "text-yellow-500 border border-yellow-300 bg-yellow-100 rounded-md"
              : "text-red-500 border border-red-300 bg-red-100 rounded-md";
        return (
          <div
            className={`font-medium ${statusClass} inline-flex items-start gap-2 px-2 py-1 text-xs font-semibold leading-4`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </div>
        );
      },
    }),
    columnHelper.accessor("discountable", {
      header: "Discountable",
      cell: (info) => {
        const isDiscountable = info.getValue();
        return (
          <span
            className={`px-2 py-1 rounded text-xs font-semibold ${
              isDiscountable
                ? "bg-green-50 text-green-600 border border-green-200"
                : "bg-gray-50 text-gray-500 border border-gray-200"
            }`}
          >
            {isDiscountable ? "Yes" : "No"}
          </span>
        );
      },
    }),
    columnHelper.accessor("metadata.weekly_discount_enabled", {
      header: () => (
        <div className="flex items-center gap-2">
          <span>Enrolled in rolling discounts</span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <HelpCircle className="h-4 w-4 text-gray-400" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Contact us for more information</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      ),
      cell: (info) => {
        const isDiscountable = info.getValue();
        return (
          <span
            className={`px-2 py-1 rounded text-xs font-semibold ${
              isDiscountable
                ? "bg-green-50 text-green-600 border border-green-200"
                : "bg-gray-50 text-gray-500 border border-gray-200"
            }`}
          >
            {isDiscountable ? "Yes" : "No"}
          </span>
        );
      },
    }),
    columnHelper.accessor((row) => row.variants.length, {
      id: "variety",
      header: "Variants",
      cell: (info) => <div className="font-medium">{info.getValue()}</div>,
    }),
    columnHelper.accessor("id", {
      id: "actions",
      header: "",
      cell: (info) => {
        const product = info.row.original;

        const onProductClick = () => {
          navigate(routes.product(product.id));
        };

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuItem
                onClick={() =>
                  navigator.clipboard.writeText(String(product.id))
                }
              >
                Copy Product ID
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onProductClick}>
                View Product Details
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    }),
  ];

  return { columns };
};

export { useProducts };
